import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../../state/auth.js';
import { hasModule, lockOf } from '../../utils/plan.js';
import { resetAiStatus, aiError } from '../../utils/ai.js';
import Card from './Card.jsx';
import Button from './Button.jsx';

// Instellingen › AI-schrijfhulp: the club brings its own AI account. One provider and key
// per club; the key is stored encrypted and never shown again.

const GUIDES = {
  anthropic: { site: 'console.anthropic.com', steps: ['Maak een account aan voor de club (liefst met een algemeen clubadres).', 'Koop tegoed bij Billing en stel bij Limits een maandlimiet in.', 'Maak bij API keys een nieuwe sleutel aan en plak die hieronder.'] },
  openai: { site: 'platform.openai.com', steps: ['Maak een account aan voor de club (liefst met een algemeen clubadres).', 'Koop tegoed bij Billing en stel bij Limits een maandbudget in.', 'Maak bij API keys een nieuwe geheime sleutel aan en plak die hieronder.'] },
  mistral: { site: 'console.mistral.ai', steps: ['Maak een account aan voor de club. Mistral is een Europese aanbieder.', 'Kies bij Billing een abonnement of tegoed, met een limiet.', 'Maak bij API keys een nieuwe sleutel aan en plak die hieronder.'] },
  gemini: { site: 'aistudio.google.com', steps: ['Log in met een Google-account van de club.', 'Kies "Get API key" en maak een sleutel aan.', 'Koppel voor meer gebruik een betaalaccount in Google Cloud en stel daar een budget in.'] },
};
const KIND_LABELS = {
  profile_bio: 'Profiel: over mij', profile_contact_for: 'Profiel: waarvoor bij mij terecht', profile_fun_fact: 'Profiel: leuk feitje',
  task_description: 'Taak: omschrijving', task_comment: 'Taak: reactie', news_content: 'Nieuwsbericht',
  meeting_agenda_description: 'Vergadering: toelichting agendapunt', meeting_minutes: 'Vergadering: notulen', meeting_decision: 'Vergadering: besluit',
  minutes_points: 'Vergadering: besluiten en actiepunten uit notulen', volunteer_description: 'Klussenbord: omschrijving', volunteer_requirements: 'Klussenbord: wat je meebrengt',
  goal_vision: 'Visie', goal_mission: 'Missie', goal_values: 'Kernwaarden', focus_area_description: 'Speerpunt', goal_description: 'Doel', goal_update_note: 'Doel: voortgang',
  event_description: 'Kalender', facility_description: 'Storing', welcome_message: 'Welkomsttekst', handover_note: 'Overdracht',
  pride_reason: 'Vrijwilliger van de maand', pride_moment: 'Trotsmoment', thanks_message: 'Bedankje', document_description: 'Document', policy_description: 'Beleidsstuk',
  template_description: 'Sjabloon: omschrijving', template_step_description: 'Sjabloon: stap', template_steps: 'Sjabloon: stappen uit beschrijving', reward_description: 'Beloning', marketplace_description: 'Marktplaats',
};
const MODEL_ERRORS = {
  ai_key_required: 'Plak eerst de sleutel van de club.',
  ai_key_format: 'Dit lijkt geen geldige sleutel. Kopieer hem opnieuw, zonder spaties.',
  ai_no_models: 'Deze sleutel geeft geen tekstmodellen. Controleer of het een API-sleutel is.',
  ai_model_required: 'Kies een model.',
};
const errorText = err => MODEL_ERRORS[err?.code] || aiError(err);

export default function AiSettingsCard() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ provider: 'anthropic', api_key: '', model: '' });
  const [models, setModels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const inPlan = hasModule('ai');

  const load = () => api('/api/ai-help/settings').then(d => {
    setData(d); setLoadFailed(false);
    if (d.configured) setForm({ provider: d.provider, api_key: '', model: d.model });
  }).catch(() => { setData(null); setLoadFailed(true); });
  useEffect(() => { if (inPlan) load(); }, [inPlan]);

  if (!inPlan) {
    const lock = lockOf('ai');
    return (
      <Card title="✨ AI-schrijfhulp" style="margin-top:24px">
        <p class="text-sm text-muted" style="margin:0">🔒 Onderdeel van {lock?.tierLabel || 'Professional'}. Met een eigen AI-account van de club helpt de app bij het schrijven van taken, nieuws, profielteksten en meer.</p>
      </Card>
    );
  }
  if (!data) {
    return loadFailed ? (
      <Card title="✨ AI-schrijfhulp" style="margin-top:24px">
        <p class="text-sm text-muted" style="margin:0">De instellingen konden niet worden geladen. Vernieuw de pagina om het opnieuw te proberen.</p>
      </Card>
    ) : null;
  }

  const guide = GUIDES[form.provider];
  const sameProvider = data.configured && data.provider === form.provider;
  const setField = (key, value) => { setForm(f => ({ ...f, [key]: value })); setMsg(null); if (key !== 'model') setModels(null); };

  async function run(fn) {
    setBusy(true); setMsg(null);
    try { await fn(); } catch (err) { setMsg({ error: errorText(err) }); }
    setBusy(false);
  }
  const fetchModels = () => run(async () => {
    const { models: list } = await api('/api/ai-help/models', { method: 'POST', body: JSON.stringify({ provider: form.provider, api_key: form.api_key }) });
    setModels(list);
    if (!list.some(m => m.id === form.model)) setForm(f => ({ ...f, model: list[0].id }));
    setMsg({ ok: `De sleutel werkt. ${list.length} model${list.length === 1 ? '' : 'len'} beschikbaar.` });
  });
  const save = () => run(async () => {
    await api('/api/ai-help/settings', { method: 'PUT', body: JSON.stringify(form) });
    await load(); setModels(null); resetAiStatus();
    setMsg({ ok: 'Opgeslagen. Leden met het recht "AI-schrijfhulp" zien nu de ✨-knop.' });
  });
  const remove = () => run(async () => {
    await api('/api/ai-help/settings', { method: 'DELETE' });
    setConfirmRemove(false); setForm({ provider: 'anthropic', api_key: '', model: '' }); setModels(null);
    await load(); resetAiStatus();
    setMsg({ ok: 'De sleutel is verwijderd. De ✨-knoppen zijn weg.' });
  });

  const label = 'display:block;font-size:13px;font-weight:600;margin-bottom:4px';
  return (
    <Card title="✨ AI-schrijfhulp" style="margin-top:24px">
      <div style="display:flex;flex-direction:column;gap:14px">
        <p class="text-sm text-muted" style="margin:0;line-height:1.6">
          De club gebruikt een eigen AI-account. De kosten lopen via dat account, en de club houdt zelf de controle met een limiet bij de aanbieder.
          De app stuurt alleen de tekst van het veld en een korte context mee (zoals de titel), nooit e-mailadressen, telefoonnummers of VOG's.
          {data.configured && <> Teksten gaan naar <strong>{data.provider_label}</strong>.</>}
        </p>

        {data.configured && (
          <div class="text-sm" style="padding:8px 12px;border-radius:8px;background:var(--color-bg-secondary);border:1px solid var(--color-border)">
            Ingesteld: <strong>{data.provider_label}</strong> · model {data.model} · sleutel eindigt op …{data.key_hint}
          </div>
        )}

        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px">
          <div>
            <label style={label} for="ai-provider">Aanbieder</label>
            <select id="ai-provider" class="form-select" style="width:100%" value={form.provider} disabled={busy} onChange={e => setField('provider', e.target.value)}>
              {data.providers.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label style={label} for="ai-key">API-sleutel</label>
            <input id="ai-key" class="form-input" style="width:100%;box-sizing:border-box" type="password" autocomplete="off" spellcheck={false} disabled={busy}
              placeholder={sameProvider ? `Leeg laten om …${data.key_hint} te houden` : 'Plak hier de sleutel'}
              value={form.api_key} onInput={e => setField('api_key', e.target.value)} />
          </div>
        </div>

        {guide && !sameProvider && (
          <div class="text-sm" style="line-height:1.6">
            <div style="font-weight:600;margin-bottom:2px">Zo krijg je een sleutel ({guide.site})</div>
            <ol style="margin:0;padding-left:18px;list-style:decimal">{guide.steps.map(s => <li key={s}>{s}</li>)}</ol>
          </div>
        )}

        <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end">
          <Button variant="secondary" size="sm" onClick={fetchModels} disabled={busy || (!form.api_key.trim() && !sameProvider)}>
            {busy && !models ? 'Bezig…' : 'Test sleutel en haal modellen op'}
          </Button>
          {models && (
            <div style="min-width:220px;flex:1">
              <label style={label} for="ai-model">Model</label>
              <select id="ai-model" class="form-select" style="width:100%" value={form.model} onChange={e => setField('model', e.target.value)}>
                {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {msg?.error && <div class="alert alert-error" style="margin:0">{msg.error}</div>}
        {msg?.ok && <div class="text-sm" style="color:var(--color-success,#16a34a)">✓ {msg.ok}</div>}

        <div style="display:flex;gap:8px;flex-wrap:wrap">
          <Button onClick={save} disabled={busy || !models || !form.model}>Opslaan</Button>
          {data.configured && !confirmRemove && <Button variant="secondary" onClick={() => setConfirmRemove(true)} disabled={busy}>Sleutel verwijderen</Button>}
          {confirmRemove && (
            <>
              <Button variant="danger" onClick={remove} disabled={busy}>Ja, verwijderen</Button>
              <Button variant="secondary" onClick={() => setConfirmRemove(false)} disabled={busy}>Annuleren</Button>
            </>
          )}
        </div>
        {!models && data.configured && <p class="text-sm text-muted" style="margin:0">Ander model of andere sleutel? Test eerst de sleutel; daarna kun je kiezen en opslaan.</p>}

        <div>
          <div style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.04em;color:var(--color-text-muted);margin-bottom:6px">Gebruik</div>
          {data.usage.length === 0 ? (
            <p class="text-sm text-muted" style="margin:0">Nog niet gebruikt deze en vorige maand.</p>
          ) : (
            <div style="overflow-x:auto">
              <table style="width:100%;border-collapse:collapse;font-size:13px">
                <thead><tr style="text-align:left;color:var(--color-text-muted)"><th style="padding:4px 8px 4px 0">Maand</th><th style="padding:4px 8px">Onderdeel</th><th style="padding:4px 8px;text-align:right">Voorstellen</th><th style="padding:4px 0 4px 8px;text-align:right">Gelukt</th></tr></thead>
                <tbody>
                  {data.usage.map(u => (
                    <tr key={`${u.month}-${u.kind}`} style="border-top:1px solid var(--color-border)">
                      <td style="padding:4px 8px 4px 0;font-variant-numeric:tabular-nums">{u.month}</td>
                      <td style="padding:4px 8px">{KIND_LABELS[u.kind] || u.kind}</td>
                      <td style="padding:4px 8px;text-align:right;font-variant-numeric:tabular-nums">{u.requests}</td>
                      <td style="padding:4px 0 4px 8px;text-align:right;font-variant-numeric:tabular-nums">{u.succeeded}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p class="text-sm text-muted" style="margin:6px 0 0">Per lid maximaal {data.limits.perUserPerHour} voorstellen per uur, en {data.limits.perClubPerDay} per dag voor de hele club.</p>
        </div>
      </div>
    </Card>
  );
}
